import type { FeatureKey, ReleaseKey, ExperimentKey, DefaultValue } from './types';
// Following the example set by PostHog all our feature flags are defined in one file that can be
// imported into any app.
// https://github.com/PostHog/posthog/blob/master/frontend/src/lib/constants.tsx
//
// As such:
// - All feature flags are defined in this file
// - Feature flag's cannot have any sensitive information in their name

// MARK: Flags -----------------------------------------------------------------
//
// Flag name and default value
//
// When the flag cannot be evaluated or there is an error, the default value is returned. This
// includes when the flag has not been created, or a network issue prevents the flag from being
// fetched.
export const FLAGS = {
  DEMO_FEATURE: false,
  DEMO_RELEASE: false,
  DEMO_EXPERIMENT: false,
  SHOW_REACT_QUERY_DEV_TOOLS_FEATURE: false,
  INCENTIVE_PROGRAM_RELEASE: false,
  SUPPORT_TASKS_SMS_SENDING_RELEASE: false,
  SEND_TEST_PUSH_NOTIFICATION_FEATURE: false,
  BENCHMARK_ANSWER_RELEASE: false,
  STUDENT_APP_REDESIGN_RELEASE: false,
  // Shows testers a toggle that forces the redesign on or off in their browser.
  // The toggle itself only appears on local and staging.
  STUDENT_APP_REDESIGN_TOGGLE_FEATURE: false,
  // Kill switch for rendering email bodies from configuration. Off ignores every
  // seeded row and renders from the template components, so reverting the whole
  // rollout never means deleting content.
  EMAIL_BODY_FROM_DB_RELEASE: false,
  // Lets students record audio or video in the browser on file upload slides.
  // Inside the native app the recorder only shows when the app's user agent
  // reports that it has microphone and camera permissions.
  ASSESSMENT_MEDIA_RECORDING_RELEASE: false,
  // Hides support chat and every way into it, and refuses the support API. Off, the
  // default, keeps support visible whenever PostHog cannot be reached.
  HIDE_STUDENT_SUPPORT_FEATURE: false,
} as const satisfies FlagAtlas;

export type FlagKey = keyof typeof FLAGS;

// A generic type that FLAGS satisfies. This type ensures that all flags follow the naming
// convention below, and provide a default value.
export type FlagAtlas = {
  [_K in FeatureKey | ReleaseKey | ExperimentKey]: DefaultValue;
};

// PartialFlagAtlas represents the shape of evaluated flags, as returned from getAllFlags. This is
// mostly useful when passing flags via server-side rendering. This will erase the key names from
// const (they're still available at runtime). But this means you will lose intellisense if this is
// used in place of FLAGS.
export type PartialFlagAtlas = Partial<FlagAtlas>;

/**
 * Type guard to check if a string is an actual flag key defined in FLAGS.
 * This is more accurate than checking the pattern alone, as it verifies the flag actually exists.
 * @param flagKeyName - The string to check
 * @returns True if the string is a key in the FLAGS object
 */
export function isFlagKey(flagKeyName: string): flagKeyName is FlagKey {
  return flagKeyName in FLAGS;
}
